import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * The badge palette. The first five are the general status tones; the rest are
 * this product's constant vocabularies — a policy's enforcement mode, a query's
 * decision, a publish's change class, and the honest-label badge that marks a
 * field the running build does not enforce (`concept.md` principle 8).
 */
export type BnBadgeVariant =
  // general status
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "error"
  // enforcement mode (03 §2.2: Enforce | Audit | DryRun, plus a disabled object)
  | "enforce"
  | "audit"
  | "dry-run"
  | "disabled"
  // query decision (04 §2: allow | deny | reject | error)
  | "allow"
  | "deny"
  // publish change class (06 §: hot / hot-reload vs restart / rolling-restart)
  | "reload"
  | "restart"
  // SLUICE-U13 and friends: the field exists, the build ignores it
  | "not-enforced";

/** Lowercases and strips separators so `DryRun`, `dry-run` and `dryrun` agree. */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z]/g, "");
}

/**
 * Maps a policy's enforcement mode to a badge variant. `Audit` and `DryRun` are
 * behaviourally identical in this build (`internal/policy/conflict.go:41-46`),
 * so they share a tone — the label still says which one was authored. Pass
 * `"Disabled"` for an object whose `enabled` flag is false; it is not rendered
 * into the bundle at all.
 */
export function enforcementModeVariant(mode: string): BnBadgeVariant {
  switch (normalize(mode)) {
    case "enforce":
      return "enforce";
    case "audit":
      return "audit";
    case "dryrun":
      return "dry-run";
    case "disabled":
      return "disabled";
    default:
      return "neutral";
  }
}

/**
 * Maps an audit record's `decision` to a badge variant. `reject` is a refusal
 * and reads like `deny`; `error` is a failure of the gateway rather than a
 * policy decision, so it takes the warning tone and never the deny tone.
 */
export function decisionVariant(decision: string): BnBadgeVariant {
  switch (normalize(decision)) {
    case "allow":
      return "allow";
    case "deny":
    case "reject":
      return "deny";
    case "error":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * Maps a generation's change class or publish path to a badge variant. A
 * policy-only publish is a hot reload with no task restart; everything else
 * restarts the task and drops in-flight queries, which is why the two are never
 * the same colour.
 */
export function publishClassVariant(changeClass: string): BnBadgeVariant {
  switch (normalize(changeClass)) {
    case "hot":
    case "hotreload":
    case "reload":
      return "reload";
    case "restart":
    case "rollingrestart":
      return "restart";
    default:
      return "neutral";
  }
}

/**
 * bn-badge is a small status pill. It slots its label and colours it by
 * `variant`; pills are the 999px-radius, soft-background, strong-text style of
 * the design system.
 *
 * Colour alone never carries the meaning — the slotted text does — so the same
 * tone may serve two vocabularies that never appear in one column.
 *
 * Properties:
 * - `variant` — see {@link BnBadgeVariant}; `neutral` by default
 * - `dot`     — shows a small leading dot, for live/running states
 *
 * Slots: the default slot is the label. For `variant="not-enforced"` it
 * defaults to the exact wording principle 8 requires, so every place that marks
 * a dead field says the same thing.
 *
 * Events: none.
 *
 *   <bn-badge variant="allow">allow</bn-badge>
 *   <bn-badge .variant=${enforcementModeVariant(p.enforcement_mode)}>${p.enforcement_mode}</bn-badge>
 *   <bn-badge .variant=${publishClassVariant(gen.change_class)}>restart</bn-badge>
 *   <bn-badge variant="not-enforced"></bn-badge>
 */
@customElement("bn-badge")
export class BnBadge extends LitElement {
  @property({ reflect: true }) variant: BnBadgeVariant = "neutral";
  /** When true, a small leading dot is shown (useful for live/running states). */
  @property({ type: Boolean }) dot = false;

  static override styles = css`
    :host {
      display: inline-flex;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: 0.125rem 0.5rem;
      border-radius: var(--bino-radius-pill);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      line-height: 1.4;
      white-space: nowrap;
    }
    .dot {
      width: 0.4375rem;
      height: 0.4375rem;
      border-radius: 50%;
      background: currentColor;
    }

    /* General status. */
    .neutral,
    .disabled {
      background: var(--bino-neutral-bg);
      color: var(--bino-neutral);
    }
    .info,
    .reload {
      background: var(--bino-info-bg);
      color: var(--bino-info);
    }
    .success,
    .enforce {
      background: var(--bino-success-bg);
      color: var(--bino-success);
    }
    .warning,
    .restart,
    .not-enforced {
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .error {
      background: var(--bino-error-bg);
      color: var(--bino-error);
    }

    /* Query decisions get their own tokens: they are read constantly and must
       stay stable even if the generic status tones are retuned. */
    .allow {
      background: var(--bino-decision-allow-bg);
      color: var(--bino-decision-allow);
    }
    .deny {
      background: var(--bino-decision-deny-bg);
      color: var(--bino-decision-deny);
    }
    /* A shadow policy observes and never refuses — Audit and DryRun alike. */
    .audit,
    .dry-run {
      background: var(--bino-decision-shadow-bg);
      color: var(--bino-decision-shadow);
    }
  `;

  override render() {
    return html`<span class="pill ${this.variant}"
      >${this.dot ? html`<span class="dot" aria-hidden="true"></span>` : ""}<slot
        >${this.variant === "not-enforced" ? "not enforced in this build" : nothing}</slot
      ></span
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-badge": BnBadge;
  }
}
